import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Motion";
import { startDonation } from "@/lib/api";

const COMM_IMG =
  "https://images.pexels.com/photos/7202816/pexels-photo-7202816.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const tiers = [
  { key: "donate_25", label: "$25", note: "Essentials" },
  { key: "donate_50", label: "$50", note: "A week of support" },
  { key: "donate_100", label: "$100", note: "Family supports", featured: true },
  { key: "donate_250", label: "$250", note: "Rental relief" },
  { key: "donate_500", label: "$500", note: "A month of stability" },
];

const Donate = () => {
  const [selected, setSelected] = useState("donate_100");
  const [loading, setLoading] = useState(false);

  const donate = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await startDonation(selected);
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        toast.error("Unable to start checkout. Please try again.");
        setLoading(false);
      }
    } catch (e) {
      toast.error("Something went wrong starting your donation.");
      setLoading(false);
    }
  };

  return (
    <section id="donate" data-testid="donate-section" className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <div className="relative overflow-hidden rounded-2xl rounded-bl-[120px] aspect-[4/5]">
            <img
              src={COMM_IMG}
              alt="A group of women embracing in support"
              className="w-full h-full object-cover"
              data-testid="donate-image"
            />
            <div className="absolute inset-0 bg-[#1A1A1A] mix-blend-multiply opacity-[0.08]" aria-hidden="true" />
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[#C85A3C] mb-6">Make a Difference</p>
            <h2 className="font-serif-display font-light leading-[1.0] tracking-tight text-4xl md:text-6xl text-[#1A1A1A]">
              Your gift keeps a family together.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#1A1A1A]/70 max-w-xl">
              Every donation directly offsets the cost of care and housing for mothers and
              children during the months they wait for services to begin.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {tiers.map((t) => {
              const active = selected === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setSelected(t.key)}
                  data-testid={`donate-tier-${t.label.replace("$", "")}`}
                  className={`text-left rounded-xl border p-5 transition-colors duration-300 ${
                    active
                      ? "bg-[#1A1A1A] border-[#1A1A1A] text-[#F4F1EA]"
                      : "bg-white/50 border-[#1A1A1A]/15 text-[#1A1A1A] hover:border-[#C85A3C]"
                  }`}
                >
                  <div className="font-serif-display text-3xl font-light">{t.label}</div>
                  <div className={`text-xs uppercase tracking-[0.12em] mt-1 ${active ? "text-[#F4F1EA]/70" : "text-[#1A1A1A]/50"}`}>
                    {t.note}
                  </div>
                </button>
              );
            })}
          </Reveal>

          <Reveal delay={0.2} className="mt-8">
            <button
              onClick={donate}
              disabled={loading}
              data-testid="donate-submit-btn"
              className="group inline-flex items-center gap-3 bg-[#C85A3C] text-[#F4F1EA] rounded-full px-8 py-4 text-sm uppercase tracking-[0.15em] hover:scale-105 transition-transform duration-300 disabled:opacity-60 disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <Loader2 size={17} className="animate-spin" /> Redirecting…
                </>
              ) : (
                <>
                  Donate Securely
                  <Heart size={16} className="fill-current transition-transform duration-300 group-hover:scale-125" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-[#1A1A1A]/50 tracking-wide">
              Secure checkout powered by Stripe. You can use a card to give a one-time gift.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Donate;
